import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, startWith, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  CommonBookingFeatureState,
  getUnpaidSeatsBySegmentId,
  includedServicesMap,
  OrderStatus,
  orderStatus,
  SeatMapActions,
  seatMapForFlight,
  seatMapForFlightStatus,
  seatMapWithStatus,
  seatSelections,
  serviceAvailability,
} from '@fcom/common-booking/index';
import {
  Amount,
  SelectedServiceData,
  ServiceAvailability,
  ServiceCatalogSelectionsForAllTravelers,
} from '@fcom/dapi/index';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceBoundItem,
  FinnairServiceItem,
  SeatmapSvgAndControlData,
} from '@fcom/dapi/api/models';
import {
  EligibilityWithCategory,
  FlightWithStreams,
  SeatChangeEvent,
} from '@fcom/common-booking/modules/ancillaries/interfaces';
import { containsUnpaidSeats, getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { unsubscribe } from '@fcom/core/utils';
import { ButtonPosition, ButtonTheme, ModalButtons } from '@fcom/ui-components/index';
import { calculateSubTotalPrice, isAllowedToSell } from '@fcom/common-booking/modules/ancillaries/utils';
import { finShare } from '@fcom/rx/index';
import { isBoundOrFlightConfirmedOrCheckedIn, isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { AncillaryLoaderStatus } from '@fcom/common-booking/modules/ancillaries/enums';

import { ManageBookingService } from '../../../services';
import { filterFlightsFromItinerary } from '../../../utils';
import { ManageBookingFeatureState } from '../../../interfaces';

interface FinnairItineraryItemFlightWithSeatmap extends FinnairItineraryItemFlight {
  seatMap$: Observable<SeatmapSvgAndControlData>;
  seatMapStatus$: Observable<SeatMapStatus>;
}

enum Action {
  CONFIRM_NO_CHANGES,
  CONFIRM_CHANGES,
  NEXT_FLIGHT,
}

interface CtaAction {
  key: string;
  action: Action;
}

export interface SeatsUpdateProgress {
  inProgress: boolean;
  isUnsuccessful?: boolean;
}

const OK_SEATMAP_STATUS = [SeatMapStatus.PENDING, SeatMapStatus.OK, SeatMapStatus.OK_NO_AVAILABILITY];

@Component({
  selector: 'fin-check-in-seatmap',
  templateUrl: 'check-in-seatmap.component.html',
  styleUrls: ['./check-in-seatmap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInSeatmapComponent implements OnInit, OnDestroy {
  readonly Category = Category;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() order$: Observable<FinnairOrder>;
  @Input() bound$: Observable<FinnairBoundItem>;
  @Input() passengers$: Observable<FinnairPassengerItem[]>;
  @Input() ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus>;
  @Input() preSelectedSeatsData: SelectedServiceData = undefined;

  @Output()
  confirmStep: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  seatsUpdate: EventEmitter<SeatsUpdateProgress> = new EventEmitter<SeatsUpdateProgress>();

  @Output()
  checkInAssignSeat: EventEmitter<{ currentFlightId: string }> = new EventEmitter();

  @Output() switchAncillaryLoaderOff = new EventEmitter<boolean>();

  flights$: Observable<FinnairItineraryItemFlightWithSeatmap[]>;
  slideIndex$ = new BehaviorSubject(0);
  availability$: Observable<ServiceAvailability>;
  selectedModifiedPax$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  hasNextFlight$: Observable<boolean>;
  seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  includedSeats$: Observable<FinnairServiceItem>;
  flightAncillaryAvailability$: Observable<{ [fragmentId: string]: EligibilityWithCategory[] }>;
  subTotalPrice$: Observable<Amount | null>;
  updateInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  checkInAssignSeatVisible$: Subject<boolean> = new BehaviorSubject(false);
  unpaidSeats$: Observable<FinnairServiceBoundItem[]>;
  ctaAction$: Observable<CtaAction>;

  private subtotalPriceSub$: ReplaySubject<Amount & { category: Category; fragmentId: string }> = new ReplaySubject<
    Amount & { category: Category; fragmentId: string }
  >(1);

  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private manageBookingService: ManageBookingService
  ) {}

  ngOnInit(): void {
    this.seatSelections$ = this.store$.pipe(seatSelections());
    this.availability$ = this.store$.pipe(serviceAvailability());
    this.includedSeats$ = this.store$.pipe(
      includedServicesMap(),
      map((services) => services?.[Category.SEAT]),
      finShare()
    );
    this.subTotalPrice$ = calculateSubTotalPrice(this.subtotalPriceSub$, of(Category.SEAT)).pipe(
      startWith(null),
      finShare()
    );

    this.flights$ = this.bound$.pipe(
      distinctUntilChanged((a, b) => a.id === b.id),
      map((b) => filterFlightsFromItinerary(b.itinerary)),
      map((flights: FinnairItineraryItemFlight[]) =>
        flights.flatMap((itineraryItemFlight) => ({
          ...itineraryItemFlight,
          seatMap$: this.store$.pipe(seatMapForFlight(itineraryItemFlight.id)),
          seatMapStatus$: combineLatest([
            this.store$.pipe(seatMapForFlightStatus(itineraryItemFlight.id)),
            this.store$.pipe(orderStatus()),
          ]).pipe(
            map(([seatmapStatus, orderStatus]) =>
              orderStatus === OrderStatus.PENDING ? SeatMapStatus.PENDING : seatmapStatus || SeatMapStatus.INITIAL
            )
          ),
        }))
      )
    );

    this.hasNextFlight$ = combineLatest([this.slideIndex$, this.flights$]).pipe(
      map(([index, flights]) => index < flights.length - 1)
    );

    this.ctaAction$ = combineLatest([
      this.order$,
      this.slideIndex$,
      this.flights$,
      this.subTotalPrice$,
      this.seatSelections$,
      this.includedSeats$,
    ]).pipe(
      map(([order, index, allFlights, price, presentSeatSelections, includedSeats]) => {
        const isLastFlight = index === allFlights.length - 1;
        const containsSeatChanges =
          Object.entries(presentSeatSelections ?? {})
            .flatMap(([flightId, seats]) => {
              const includedSeatsForFlight =
                includedSeats?.bounds.flatMap(
                  ({ segments }) =>
                    segments
                      .filter(({ id }) => id === flightId)
                      .flatMap(({ passengers }) => passengers.flatMap(({ services }) => services))
                      .map(({ seatNumber }) => seatNumber) ?? []
                ) ?? [];
              return seats.filter(({ seatNumber }) => !includedSeatsForFlight.includes(seatNumber));
            })
            .filter(Boolean).length > 0;
        const isPriceZero = isNaN(+price?.amount) || +price?.amount === 0;

        if (!isLastFlight) {
          return {
            key: 'actions.nextFlight',
            action: Action.NEXT_FLIGHT,
          };
        }

        if (containsUnpaidSeats(order) || (containsSeatChanges && isPriceZero) || !isPriceZero) {
          return {
            key: 'MMB.checkIn.funnel.steps.seats.button.viewSummary',
            action: Action.CONFIRM_CHANGES,
          };
        }

        return {
          key: 'MMB.checkIn.funnel.steps.seats.button.confirmStep',
          action: Action.CONFIRM_NO_CHANGES,
        };
      })
    );

    this.flightAncillaryAvailability$ = combineLatest([this.availability$, this.flights$, this.order$]).pipe(
      map(([serviceAvailability, flights, order]) => {
        return flights.reduce((flightEligibilityMap, flight) => {
          const isConfirmedOrCheckedIn = isBoundOrFlightConfirmedOrCheckedIn(flight);
          const allowedToSell = isAllowedToSell(Category.SEAT, order, serviceAvailability, flight);

          flightEligibilityMap[flight.id] = allowedToSell.map((eligibility) => {
            return {
              ...eligibility,
              isAllowedToUse: isConfirmedOrCheckedIn && eligibility.isAllowedToUse,
            };
          });
          return flightEligibilityMap;
        }, {});
      }),
      distinctUntilChanged(),
      finShare()
    );

    const currentFlight$ = combineLatest([this.slideIndex$, this.flights$]).pipe(
      map(([index, flights]) => flights[index])
    );

    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$]).subscribe(([index, flights]) => {
        this.setCheckInAssignSeatEligible(flights[index]?.id);
      })
    );

    this.subscriptions.add(
      currentFlight$
        .pipe(
          switchMap((flight) => this.store$.pipe(seatMapWithStatus(flight.id), take(1))),
          filter(
            (currentSeatMapStatus) => !currentSeatMapStatus || !OK_SEATMAP_STATUS.includes(currentSeatMapStatus.status)
          ),
          switchMap(() => combineLatest([currentFlight$, this.order$]).pipe(take(1)))
        )
        .subscribe(([currentFlight, order]) => {
          this.getSeatMap(order, currentFlight);
        })
    );

    if (this.preSelectedSeatsData) {
      if (this.preSelectedSeatsData.fragmentId) {
        this.subscriptions.add(
          this.flights$.pipe(take(1)).subscribe((flights) => {
            const flightIndexSelected = flights.findIndex(
              (flight) => flight.id === this.preSelectedSeatsData.fragmentId
            );
            if (flightIndexSelected) {
              this.slideIndex$.next(flightIndexSelected);
            }
          })
        );
      }
      if (this.preSelectedSeatsData.paxId) {
        this.selectedModifiedPax$.next(this.preSelectedSeatsData.paxId);
      }
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  loadSeatMapForFlightFromOrder(flight: FinnairItineraryItemFlight): void {
    this.subscriptions.add(this.order$.pipe(take(1)).subscribe((order) => this.getSeatMap(order, flight)));
  }

  changeSeats({ seatNumber, flightId, travelerId }: SeatChangeEvent): void {
    this.subscriptions.add(
      this.flights$
        .pipe(
          take(1),
          map((flights) => flights.find(({ id }) => id === flightId)),
          switchMap((flight) => flight.seatMap$)
        )
        .subscribe((seatMap) => {
          const seatSelected = seatMap.seats.find((seat) => seat.seatNumber === seatNumber);

          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId,
              travelerId,
              seat: seatSelected,
            })
          );
        })
    );
  }

  continue(action: Action): void {
    switch (action) {
      case Action.NEXT_FLIGHT: {
        this.slideIndex$.next(this.slideIndex$.value + 1);
        break;
      }
      case Action.CONFIRM_CHANGES: {
        this.updateServices();
        break;
      }
      case Action.CONFIRM_NO_CHANGES: {
        this.confirmStep.emit();
        break;
      }
      default: {
        this.confirmStep.emit();
        break;
      }
    }
  }

  subtotalChange(value: Amount, fragmentId: string): void {
    this.subtotalPriceSub$.next({ ...value, category: Category.SEAT, fragmentId });
  }

  flightsTrackBy(_index: number, flight: FlightWithStreams): string {
    return flight.id;
  }

  private updateServices(): void {
    this.subscriptions.add(
      this.bound$.pipe(take(1)).subscribe((bound) => {
        this.seatsUpdate.emit({ inProgress: true });
        const [requests] = getServiceData(this.store$, Category.SEAT, [bound], bound.itinerary.filter(isFlight));
        this.updateInProgress$.next(true);
        const didOperation = this.manageBookingService.setServices(requests, Category.SEAT, {
          next: () => {
            this.updateInProgress$.next(false);
            this.seatsUpdate.emit({ inProgress: false });
            this.confirmStep.emit();
          },
          error: () => {
            this.updateInProgress$.next(false);
            this.seatsUpdate.emit({ inProgress: false, isUnsuccessful: true });
          },
        });

        if (!didOperation) {
          this.updateInProgress$.next(false);
          this.seatsUpdate.emit({ inProgress: false });
          this.confirmStep.emit();
        }
      })
    );
  }

  private getSeatMap(order: FinnairOrder, flight: FinnairItineraryItemFlight): void {
    this.manageBookingService.triggerFetchSeatMap(
      flight,
      order.id,
      order.hash,
      order.prices?.included?.total?.total?.totalAmount?.currencyCode ?? 'EUR'
    );
  }

  checkInAssignFreeSeats(): void {
    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$])
        .pipe(take(1))
        .subscribe(([slideIndex, flights]) => {
          const currentFlight = flights[slideIndex];
          this.checkInAssignSeat.emit({ currentFlightId: currentFlight.id });
        })
    );
  }

  private setCheckInAssignSeatEligible(assignSeatFlightId: string) {
    this.unpaidSeats$ = this.store$.pipe(getUnpaidSeatsBySegmentId(assignSeatFlightId));
    this.subscriptions.add(
      this.unpaidSeats$
        .pipe(
          map((unpaidSeats) =>
            unpaidSeats.some((boundItem) => boundItem.segments.some((segment) => segment.id === assignSeatFlightId))
          )
        )
        .subscribe((showCheckInAssignSeat) => {
          this.checkInAssignSeatVisible$.next(showCheckInAssignSeat);
        })
    );
  }

  emitSwitchAncillaryLoaderOff(event: boolean): void {
    this.switchAncillaryLoaderOff.emit(event);
  }
}
